import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Upload, Download, Pencil, Trash2, History, FileSpreadsheet, Loader2 } from "lucide-react";
import { api, formatCurrency, getErrorMessage } from "@/lib/api";
import { toast } from "sonner";

const MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Aoû", "Sep", "Oct", "Nov", "Déc"];

export default function HistoricalImportsManager({ onChanged }) {
  const [entries, setEntries] = useState([]);
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [deleteImport, setDeleteImport] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newRow, setNewRow] = useState({ year: new Date().getFullYear() - 1, month: 1, orders_amount: 0, ca_amount: 0 });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await api.listHistoricalImports();
      setEntries(res.data.entries || []);
      setImports(res.data.imports || []);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      await api.uploadHistoricalImportExcel(file, file.name);
      toast.success("Fichier importé");
      await fetchAll();
      onChanged?.();
    } catch (e) {
      toast.error(getErrorMessage(e, "Échec de l'import Excel"));
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.downloadHistoricalImportTemplate();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template_historique.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur lors du téléchargement du template");
    }
  };

  const handleSaveEdit = async () => {
    try {
      if (editEntry.id) {
        await api.updateHistoricalImport(editEntry.id, {
          year: parseInt(editEntry.year, 10),
          month: parseInt(editEntry.month, 10),
          orders_amount: parseFloat(editEntry.orders_amount) || 0,
          ca_amount: parseFloat(editEntry.ca_amount) || 0,
          note: editEntry.note || null,
        });
      } else {
        await api.upsertHistoricalImport({
          year: parseInt(editEntry.year, 10),
          month: parseInt(editEntry.month, 10),
          orders_amount: parseFloat(editEntry.orders_amount) || 0,
          ca_amount: parseFloat(editEntry.ca_amount) || 0,
          note: editEntry.note || null,
        });
      }
      toast.success("Enregistré");
      setEditEntry(null);
      await fetchAll();
      onChanged?.();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    }
  };

  const handleCreate = async () => {
    try {
      await api.upsertHistoricalImport({
        year: parseInt(newRow.year, 10),
        month: parseInt(newRow.month, 10),
        orders_amount: parseFloat(newRow.orders_amount) || 0,
        ca_amount: parseFloat(newRow.ca_amount) || 0,
      });
      toast.success("Ligne ajoutée");
      setShowCreate(false);
      setNewRow({ year: new Date().getFullYear() - 1, month: 1, orders_amount: 0, ca_amount: 0 });
      await fetchAll();
      onChanged?.();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    }
  };

  const handleDeleteEntry = async (id) => {
    try {
      await api.deleteHistoricalImportEntry(id);
      toast.success("Supprimé");
      await fetchAll();
      onChanged?.();
    } catch {
      toast.error("Erreur");
    }
  };

  const handleDeleteImport = async () => {
    if (!deleteImport) return;
    try {
      await api.deleteHistoricalImportBatch(deleteImport.import_id);
      toast.success(`Import « ${deleteImport.label} » supprimé`);
      setDeleteImport(null);
      await fetchAll();
      onChanged?.();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="font-heading flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            Imports historiques (fantômes)
          </CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid="download-template">
              <Download className="h-3.5 w-3.5 mr-1" /> Template
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowCreate(true)} data-testid="add-manual-entry">
              <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Ajout manuel
            </Button>
            <label className="cursor-pointer">
              <Button size="sm" disabled={uploading} asChild>
                <span data-testid="upload-excel">
                  {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
                  Importer Excel
                </span>
              </Button>
              <input
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => handleUpload(e.target.files?.[0])}
              />
            </label>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Ces données s'ajoutent visuellement aux graphiques sans créer de fausses commandes.
          Pour un mois donné : si une donnée réelle existe (ex : 15k€), l'import (ex : 150k€) la complète à hauteur du delta (135k€).
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {imports.length > 0 && (
          <div>
            <p className="text-xs font-medium mb-2">Imports en lot</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {imports.map((imp) => (
                <div key={imp.import_id} className="flex items-center justify-between border rounded p-2 bg-zinc-50">
                  <div className="text-xs">
                    <p className="font-medium">{imp.label}</p>
                    <p className="text-muted-foreground">{imp.count} mois · {formatCurrency(imp.total_ca_amount)} CA</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setDeleteImport(imp)} className="h-7 w-7 p-0">
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : entries.length === 0 ? (
          <p className="text-center text-muted-foreground text-sm py-6">
            Aucun import historique. Télécharge le template et glisse-le ici pour démarrer.
          </p>
        ) : (
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Année</TableHead>
                  <TableHead className="text-xs">Mois</TableHead>
                  <TableHead className="text-xs text-right">Commandes</TableHead>
                  <TableHead className="text-xs text-right">CA</TableHead>
                  <TableHead className="text-xs">Note</TableHead>
                  <TableHead className="text-xs w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((e) => (
                  <TableRow key={e.id} data-testid={`historic-row-${e.year}-${e.month}`}>
                    <TableCell className="text-sm">{e.year}</TableCell>
                    <TableCell className="text-sm">{MONTHS[e.month - 1]}</TableCell>
                    <TableCell className="text-sm text-right font-mono">{formatCurrency(e.orders_amount || 0)}</TableCell>
                    <TableCell className="text-sm text-right font-mono">{formatCurrency(e.ca_amount || 0)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground truncate max-w-32">{e.note || "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setEditEntry({ ...e })} className="h-7 w-7 p-0">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteEntry(e.id)} className="h-7 w-7 p-0">
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={!!editEntry} onOpenChange={(o) => !o && setEditEntry(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modifier l'entrée historique</DialogTitle>
            </DialogHeader>
            {editEntry && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Année</Label>
                  <Input type="number" value={editEntry.year} onChange={(e) => setEditEntry({ ...editEntry, year: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Mois (1-12)</Label>
                  <Input type="number" min={1} max={12} value={editEntry.month} onChange={(e) => setEditEntry({ ...editEntry, month: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Commandes (€)</Label>
                  <Input type="number" step="0.01" value={editEntry.orders_amount}
                    onChange={(e) => setEditEntry({ ...editEntry, orders_amount: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">CA (€)</Label>
                  <Input type="number" step="0.01" value={editEntry.ca_amount}
                    onChange={(e) => setEditEntry({ ...editEntry, ca_amount: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Note</Label>
                  <Input value={editEntry.note || ""} onChange={(e) => setEditEntry({ ...editEntry, note: e.target.value })} />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditEntry(null)}>Annuler</Button>
              <Button onClick={handleSaveEdit} data-testid="save-historic-edit">Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ajouter un mois historique</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Année</Label>
                <Input type="number" value={newRow.year} onChange={(e) => setNewRow({ ...newRow, year: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Mois</Label>
                <Input type="number" min={1} max={12} value={newRow.month} onChange={(e) => setNewRow({ ...newRow, month: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Commandes (€)</Label>
                <Input type="number" step="0.01" value={newRow.orders_amount}
                  onChange={(e) => setNewRow({ ...newRow, orders_amount: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">CA (€)</Label>
                <Input type="number" step="0.01" value={newRow.ca_amount}
                  onChange={(e) => setNewRow({ ...newRow, ca_amount: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
              <Button onClick={handleCreate} data-testid="save-new-historic">Ajouter</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete batch import */}
        <AlertDialog open={!!deleteImport} onOpenChange={(o) => !o && setDeleteImport(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer cet import en lot ?</AlertDialogTitle>
              <AlertDialogDescription>
                Toutes les {deleteImport?.count} entrées de « {deleteImport?.label} » seront supprimées.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteImport} className="bg-destructive text-destructive-foreground">
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
